import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Snackbar from '@/components/Snackbar.vue';
import { VALIDATION_RULES } from '@/constants/ValidationRules';
import { CONTRACT_PLACEHOLDERS, SIGNATURE_PLACEHOLDERS } from '@/constants/ContractPlaceholders';
import { existingTemplateKeys } from '@/utils/templateKeys';
const FIELD_KINDS = [
    { id: 'text', title: 'Text' },
    { id: 'variable', title: 'Údaj' },
    { id: 'signature', title: 'Podpis' },
];
// Both value and key are always present so Vue can track them when the kind changes.
const NEW_FIELD = { kind: 'text', value: '', key: undefined, page: 1, x: 50, y: 50, width: 120, fontSize: 10 };
function toBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
}
// pdfjs is a heavy dependency; it only loads once a form is actually on screen.
let DocumentTemplateEditor = class DocumentTemplateEditor extends Vue {
    value;
    base;
    editor = null;
    templateKey = '';
    title = '';
    affectsTaxes = false;
    validFrom = '';
    validTo = '';
    fields = [];
    promptHtml = '';
    pdfBytes = null;
    loadingBasePdf = false;
    page = 1;
    pageCount = 1;
    selected = null;
    saving = false;
    rules = VALIDATION_RULES;
    fieldKinds = FIELD_KINDS;
    get keyItems() {
        return existingTemplateKeys();
    }
    get open() {
        return this.value;
    }
    set open(value) {
        this.$emit('input', value);
    }
    /** pdfjs keeps the array it is handed, so the canvas always gets its own copy. */
    get pdfSource() {
        return this.pdfBytes ? this.pdfBytes.slice() : null;
    }
    mounted() {
        this.editor = new Editor({
            extensions: [StarterKit],
            content: '',
            onUpdate: ({ editor }) => {
                this.promptHtml = editor.getHTML();
            },
        });
        this.load();
    }
    beforeDestroy() {
        this.editor?.destroy();
    }
    onOpen(value) {
        if (value)
            this.load();
    }
    async load() {
        this.templateKey = this.base?.data.key ?? '';
        this.title = this.base?.data.title ?? '';
        this.affectsTaxes = this.base?.data.affectsTaxes ?? false;
        this.validFrom = this.base?.data.validFrom ?? '';
        this.validTo = this.base?.data.validTo ?? '';
        this.fields = (this.base?.data.fields ?? []).map(field => ({ value: undefined, key: undefined, ...field }));
        this.promptHtml = this.base?.data.promptHtml ?? '';
        this.editor?.commands.setContent(this.promptHtml);
        this.page = 1;
        this.selected = null;
        this.pdfBytes = null;
        // A new version keeps the same form, so the blank PDF is re-sent with it.
        if (this.base)
            await this.loadBasePdf(this.base);
    }
    async loadBasePdf(base) {
        this.loadingBasePdf = true;
        try {
            const url = await this.$store.dispatch('document/templatePdfUrl', base);
            this.pdfBytes = new Uint8Array(await (await fetch(url)).arrayBuffer());
        }
        catch (e) {
            console.error(e);
            Snackbar.error('Formulář předchozí verze se nepodařilo načíst');
        }
        finally {
            this.loadingBasePdf = false;
        }
    }
    async pickPdf(file) {
        if (!file)
            return;
        this.pdfBytes = new Uint8Array(await file.arrayBuffer());
        this.page = 1;
    }
    keysFor(kind) {
        return kind === 'signature'
            ? CONTRACT_PLACEHOLDERS.filter(p => SIGNATURE_PLACEHOLDERS.includes(p.key))
            : CONTRACT_PLACEHOLDERS.filter(p => !SIGNATURE_PLACEHOLDERS.includes(p.key));
    }
    onKindChange(field) {
        field.key = field.kind === 'text' ? undefined : this.keysFor(field.kind)[0].key;
        if (field.kind === 'text')
            field.value = field.value ?? '';
    }
    addField() {
        this.fields.push({ ...NEW_FIELD, page: this.page });
        this.selected = this.fields.length - 1;
    }
    periodRule() {
        return !this.validFrom || !this.validTo || this.validFrom <= this.validTo || 'Platnost do musí být po platnosti od';
    }
    close() {
        this.open = false;
    }
    async save() {
        if (!this.$refs.form.validate())
            return;
        if (!this.pdfBytes) {
            Snackbar.error('Vyberte PDF formulář');
            return;
        }
        if (this.saving)
            return;
        this.saving = true;
        try {
            await this.$store.dispatch('document/storeTemplate', {
                key: this.templateKey,
                title: this.title,
                pdfBase64: toBase64(this.pdfBytes),
                fields: this.fields,
                promptHtml: this.promptHtml,
                affectsTaxes: this.affectsTaxes,
                validFrom: this.validFrom || null,
                validTo: this.validTo || null,
            });
            Snackbar.success('Verze dokumentu uložena');
            this.open = false;
        }
        catch (e) {
            console.error(e);
            Snackbar.error(e.message || 'Uložení se nezdařilo');
        }
        finally {
            this.saving = false;
        }
    }
};
__decorate([
    Prop({ default: false })
], DocumentTemplateEditor.prototype, "value", void 0);
__decorate([
    Prop({ default: null })
], DocumentTemplateEditor.prototype, "base", void 0);
__decorate([
    Watch('value')
], DocumentTemplateEditor.prototype, "onOpen", null);
DocumentTemplateEditor = __decorate([
    Component({ components: { EditorContent, PdfFieldCanvas: () => import('@/components/PdfFieldCanvas.vue') } })
], DocumentTemplateEditor);
export default DocumentTemplateEditor;
